/**
 * StartTrial — Toothmatrix-style white-label onboarding.
 *
 * Steps:
 *   1. Pick a brand name + accent color + upload logo (optional)
 *   2. Create an account (or sign in) → routes through /signup
 *      and onboards a tenant with a 7-day trial.
 *   3. After signup completes the user lands on /dashboard with the trial active.
 *
 * If the visitor is already logged in we just call the onboard endpoint with
 * their existing session.
 */
import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles, Image as ImageIcon, X, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import Logo from '@/components/Logo';
import WhitelabelDemo from '@/components/WhitelabelDemo';
import api from '@/lib/api';

const PALETTE = ['#8B3FC5', '#0A4D8C', '#0F766E', '#7C3AED', '#DC2626', '#0A0A0A', '#475569', '#06B6D4'];

/**
 * Compute the sub-domain slug a tenant will receive — mirrors the backend
 * generator in routes_tenants.onboard_tenant. This is just for the preview
 * pane (the server will redo it & may add a `-2` suffix if there's a clash).
 */
function previewSlug(brandName) {
  if (!brandName) return 'your-brand';
  const s = brandName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 24);
  return s || 'your-brand';
}

export default function StartTrial() {
  /* eslint-disable -- v6 react-hooks/set-state-in-effect + immutability false-positives on the resume + auto-onboard effects */
  const { user } = useAuth();
  const nav = useNavigate();
  const fileRef = useRef(null);
  // 0: brand, 1: logo, 2: preview, 3: google
  const [step, setStep] = useState(0);
  const [brand, setBrand] = useState('');
  const [color, setColor] = useState('#8B3FC5');
  const [logoUrl, setLogoUrl] = useState('');
  const [busy, setBusy] = useState(false);

  const fqdn = `${previewSlug(brand)}.vigilantentities.com`;

  // Resume from email — pre-fill form if URL has ?resume=jwt
  React.useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const token = sp.get('resume');
    if (!token) return;
    api.get(`/trial/draft/${token}`)
      .then(({ data }) => {
        setBrand(data.brand_name || '');
        setColor(data.primary_color || '#8B3FC5');
        setLogoUrl(data.logo_url || '');
        setSaveEmail(data.email || '');
        setSavedProgress(true);
        setStep(2); // Land them on Preview
        toast.success(`Welcome back — picking up your ${data.brand_name} workspace.`, { duration: 5000 });
      })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Resume link is invalid.'));
  }, []);

  const [saveEmail, setSaveEmail] = useState('');
  const [savedProgress, setSavedProgress] = useState(false);
  const [savingProgress, setSavingProgress] = useState(false);

  const saveProgress = async () => {
    if (!saveEmail.trim() || !brand.trim()) return;
    setSavingProgress(true);
    try {
      await api.post('/trial/save-progress', {
        email: saveEmail.trim().toLowerCase(),
        brand_name: brand.trim(),
        primary_color: color,
        logo_url: logoUrl || null,
        slug_preview: previewSlug(brand),
        last_step: 'preview',
      });
      setSavedProgress(true);
      toast.success("Saved. If you don't activate today we'll send you a resume link tomorrow.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save.');
    } finally { setSavingProgress(false); }
  };

  // localStorage so values survive the Google OAuth redirect
  const stash = () => {
    localStorage.setItem('ve_trial_brand', brand);
    localStorage.setItem('ve_trial_color', color);
    if (logoUrl) localStorage.setItem('ve_trial_logo', logoUrl);
  };

  // If already logged in, skip Google and directly onboard
  const onboardNow = async () => {
    if (!brand.trim()) { toast.error('Please pick a brand name.'); setStep(0); return; }
    setBusy(true);
    try {
      const { data } = await api.post('/tenants/onboard', {
        brand_name: brand.trim(),
        primary_color: color,
        logo_url: logoUrl || null,
      });
      toast.success(`Trial activated — workspace live at ${data?.fqdn || 'your sub-domain'}.`, { duration: 6000 });
      ['ve_trial_brand', 've_trial_color', 've_trial_logo'].forEach((k) => localStorage.removeItem(k));
      nav('/dashboard');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not activate trial.');
    } finally { setBusy(false); }
  };

  const uploadLogo = async (f) => {
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error('Logo must be under 4 MB.'); return; }
    const fd = new FormData();
    fd.append('file', f);
    fd.append('kind', 'tenant_logo');
    setBusy(true);
    try {
      const { data } = await api.post('/documents/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setLogoUrl(data.url || data.public_url || data.download_url || '');
      toast.success('Logo uploaded.');
    } catch { toast.error('Upload failed.'); }
    finally { setBusy(false); }
  };

  // After Google redirect lands us here with ?after=google we auto-onboard
  React.useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('after') === 'google' && user) {
      const b = localStorage.getItem('ve_trial_brand') || '';
      const c = localStorage.getItem('ve_trial_color') || '#8B3FC5';
      const l = localStorage.getItem('ve_trial_logo') || '';
      setBrand(b); setColor(c); setLogoUrl(l);
      if (b) {
        // small delay so React state settles
        setTimeout(() => {
          api.post('/tenants/onboard', { brand_name: b, primary_color: c, logo_url: l || null })
            .then(() => { toast.success('Trial activated — 7 days on us.'); nav('/dashboard'); })
            .catch((e) => toast.error(e?.response?.data?.detail || 'Could not activate trial.'));
        }, 400);
      }
    }
  }, [user, nav]);

  return (
    <main className="min-h-screen bg-[#F7F5F4]">
      <header className="h-16 flex items-center px-6 md:px-10 border-b border-slate-200 bg-white">
        <Logo size="sm"/>
      </header>

      <div className="max-w-2xl mx-auto px-6 py-12 md:py-20">
        <div className="ve-eyebrow">7-day Trial · No credit card</div>
        <h1 className="display text-4xl md:text-5xl mt-3 font-bold leading-tight">
          Make Vigilant <span className="ve-gold-text">yours</span> in 60 seconds.
        </h1>
        <p className="text-base text-slate-600 mt-4 max-w-md">
          White-label the platform with your brand and logo. Auto-onboarded with Google. Yours for 7 days, free.
        </p>

        {/* Step indicator */}
        <div className="mt-10 flex gap-2 items-center text-xs">
          {['Brand', 'Logo', 'Preview', 'Activate'].map((s, i) => (
            <React.Fragment key={s}>
              <div className={`px-3 py-1.5 rounded-full font-bold ${i === step ? 'bg-[#0A0A0A] text-[#8B3FC5]' : i < step ? 'bg-[#FFF8E5] text-[#5E1F8B]' : 'bg-slate-100 text-slate-400'}`}>
                {i + 1}. {s}
              </div>
              {i < 3 && <div className="h-px w-6 bg-slate-200"/>}
            </React.Fragment>
          ))}
        </div>

        {/* Card */}
        <div className="ve-card mt-8 p-7 md:p-10">
          {step === 0 && (
            <div data-testid="trial-step-brand">
              <label className="block text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Your brand / company name</label>
              <input
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                placeholder="e.g. Falcon Capital"
                data-testid="trial-brand-name"
                className="mt-2 w-full rounded-md border border-slate-200 px-4 py-3 text-lg focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20"
              />
              <div className="mt-7">
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-3">Pick an accent colour</div>
                <div className="flex flex-wrap gap-2">
                  {PALETTE.map((c) => (
                    <button
                      key={c}
                      onClick={() => setColor(c)}
                      data-testid={`trial-color-${c.slice(1)}`}
                      className={`h-9 w-9 rounded-full border-2 transition-all ${color === c ? 'border-[#0A0A0A] scale-110' : 'border-white hover:scale-105'}`}
                      style={{ background: c, boxShadow: '0 0 0 1px rgba(0,0,0,0.08)' }}
                      aria-label={`Color ${c}`}
                    />
                  ))}
                </div>
              </div>
              <div className="mt-9 flex justify-end">
                <button
                  onClick={() => {
                    if (!brand.trim()) return toast.error('Brand name required.');
                    setStep(1);
                  }}
                  data-testid="trial-next-1"
                  className="ve-btn-primary"
                >
                  Continue <ArrowRight size={14}/>
                </button>
              </div>
            </div>
          )}

          {step === 1 && (
            <div data-testid="trial-step-logo">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-3">Upload your logo (optional)</div>
              <div
                onClick={() => fileRef.current?.click()}
                className="rounded-lg border-2 border-dashed border-slate-200 hover:border-[#8B3FC5] cursor-pointer p-10 text-center transition-colors"
              >
                {logoUrl ? (
                  <div className="relative inline-block">
                    <img src={logoUrl} alt="logo" className="h-20 mx-auto object-contain"/>
                    <button
                      onClick={(e) => { e.stopPropagation(); setLogoUrl(''); }}
                      className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-rose-500 text-white text-xs hover:bg-rose-600"
                    ><X size={12} className="mx-auto"/></button>
                  </div>
                ) : (
                  <>
                    <ImageIcon size={32} className="mx-auto text-slate-300"/>
                    <p className="mt-3 text-sm text-slate-600">Click to upload — SVG, PNG, JPG up to 4MB</p>
                    <p className="mt-1 text-xs text-slate-400">Or skip this step</p>
                  </>
                )}
                <input
                  ref={fileRef} type="file" accept="image/*" className="hidden"
                  onChange={(e) => uploadLogo(e.target.files?.[0])}
                  data-testid="trial-logo-file"
                />
              </div>

              {/* Preview */}
              <div className="mt-8">
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-2">Preview</div>
                <div className="rounded-lg border border-slate-200 p-5 bg-white flex items-center gap-3">
                  {logoUrl ? <img src={logoUrl} className="h-10 w-10 object-contain rounded"/> : <div className="h-10 w-10 rounded" style={{ background: color }}/>}
                  <div>
                    <div className="display text-lg font-bold" style={{ color }}>{brand}</div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 mt-0.5">Powered by Vigilant</div>
                  </div>
                </div>
              </div>

              <div className="mt-9 flex justify-between">
                <button onClick={() => setStep(0)} className="ve-btn-ghost">Back</button>
                <button onClick={() => setStep(2)} data-testid="trial-next-2" className="ve-btn-primary">Preview your workspace <ArrowRight size={14}/></button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="trial-step-preview">
              <div className="ve-eyebrow" style={{ color }}>Preview · sub-domain auto-provisioned</div>
              <h2 className="display text-2xl mt-2 font-bold leading-tight">
                Here&rsquo;s what <span style={{ color }}>{brand || 'your workspace'}</span> looks like.
              </h2>
              <p className="text-sm text-slate-600 mt-2 max-w-md">
                Four interactive previews of your white-labeled platform. The sub-domain below is the one you&rsquo;ll get the moment you activate.
              </p>

              <div className="mt-6">
                <WhitelabelDemo
                  brand={brand}
                  logoUrl={logoUrl}
                  color={color}
                  fqdn={fqdn}
                />
              </div>

              {/* Save my progress — abandoned-trial follow-up hook */}
              <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="trial-save-progress">
                <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">Save my progress</div>
                <p className="text-xs text-slate-600 mt-1">
                  Drop your email — if you don&rsquo;t activate today, we&rsquo;ll send you a one-click resume link tomorrow with this preview baked in. No marketing, just the link.
                </p>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <input
                    type="email"
                    value={saveEmail}
                    onChange={(e) => setSaveEmail(e.target.value)}
                    placeholder="you@company.com"
                    data-testid="trial-save-email"
                    className="flex-1 min-w-[200px] rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#8B3FC5]"
                    disabled={savedProgress}
                  />
                  <button
                    onClick={saveProgress}
                    disabled={savingProgress || savedProgress || !saveEmail.trim() || !brand.trim()}
                    data-testid="trial-save-progress-btn"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-[#8B3FC5] bg-[#FFF8E5] text-[#7A5A00] text-xs font-bold uppercase tracking-[0.18em] hover:bg-[#8B3FC5] hover:text-white disabled:opacity-50 transition"
                  >
                    {savedProgress ? '✓ Saved' : savingProgress ? 'Saving…' : 'Save my spot'}
                  </button>
                </div>
              </div>

              <div className="mt-9 flex justify-between">
                <button onClick={() => setStep(1)} className="ve-btn-ghost">Back</button>
                <button onClick={() => setStep(3)} data-testid="trial-next-3" className="ve-btn-primary">Looks great — activate <ArrowRight size={14}/></button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="trial-step-google">
              <Sparkles size={32} className="text-[#5E1F8B]"/>
              <h2 className="display text-2xl mt-3 font-bold">One last step.</h2>
              <p className="text-sm text-slate-600 mt-2 max-w-md">
                Sign in to activate your 7-day trial of <span className="font-bold text-[#5E1F8B]">{brand}</span>.
                Your workspace will go live at:
              </p>
              <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#8B3FC5]/40 bg-[#FFF8E5] text-[#7A5A00]">
                <Globe size={12}/>
                <span className="text-sm font-mono">{fqdn}</span>
              </div>
              {user ? (
                <button
                  disabled={busy}
                  onClick={onboardNow}
                  data-testid="start-trial-submit"
                  className="mt-8 ve-btn-primary"
                >
                  {busy ? 'Activating…' : 'Activate trial as ' + user.name}
                  <ArrowRight size={14}/>
                </button>
              ) : (
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <Link
                    to="/signup"
                    onClick={stash}
                    data-testid="start-trial-submit"
                    className="ve-btn-primary"
                  >
                    Create your account <ArrowRight size={14}/>
                  </Link>
                  <Link
                    to="/login"
                    onClick={stash}
                    className="ve-btn-ghost"
                  >
                    Already have one? Sign in
                  </Link>
                </div>
              )}
              <div className="mt-8 text-xs text-slate-400">
                By continuing you agree to our terms. No payment required during the trial.
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
